import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import {
  CustomDropdown,
  MAIN_TAB_PAGE_LABEL,
  medPromptBrowserTitle,
  Table,
  ColumnCustomizationModal,
  ColumnCustomizationButton,
} from '@medprompt/shared';
import TaskModal from './TaskModal';
import SearchBar from '../detail/tabs/SearchBar';
import HelpButton from '../help/HelpButton';
import { HELP_PAGE_IDS } from '../help/helpContent';
import './OutcomeTaskView.css';
import '../list-view-page.css';
import '@medprompt/shared/src/common/modals.css';
import '@medprompt/shared/src/common/dropdowns.css';
import useOutcomeTaskList from './useOutcomeTaskList';
import useOutcomeTaskTable from './useOutcomeTaskTable';
import BulkDeleteTasksModal from './BulkDeleteTasksModal';
import BulkMarkCompleteTasksModal from './BulkMarkCompleteTasksModal';

const OutcomeTaskViewController = ({ taskViewerContext }) => {
  const highlightRowsRef = useRef(() => {});
  const list = useOutcomeTaskList(highlightRowsRef);
  const table = useOutcomeTaskTable(list, taskViewerContext);
  highlightRowsRef.current = table.setHighlightedRows;

  const { getViewerCount, getViewers } = taskViewerContext || {};

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.outcomeTasks);
  }, []);

  const handleViewModeChange = (option) => {
    list.selectViewMode(option);
    table.clearAssigneeFilter();
  };

  const handleRowClick = (task) => {
    list.openTask(task);
    table.setHighlightedRows(new Set([task.taskId]));
  };

  return (
    <div className="outcome-task-view list-view-page">
      <div className="header">
        <div className="header-title-text">
          <div className="header-title-with-help">
            <h1 className="tab-page-title">{MAIN_TAB_PAGE_LABEL.outcomeTasks}</h1>
            <HelpButton pageId={HELP_PAGE_IDS.outcomeTasks} />
          </div>
          <CustomDropdown
            label="View Mode"
            selected={list.currentViewModeName}
            selectedIcon={list.currentViewModeIcon}
            options={list.viewModeOptions}
            isOpen={list.viewModeDropdown.isOpen}
            toggleDropdown={() => list.viewModeDropdown.toggle([])}
            onSelect={handleViewModeChange}
            containerRef={list.viewModeDropdown.dropdownRef}
            width="180px"
          />
        </div>
        <div className="header-buttons">
          {list.isSearchActive && (
            <button className="header-button clear-search-button" onClick={list.handleClearSearch}>
              <FontAwesomeIcon icon={faTimesCircle} /> Clear search
            </button>
          )}
          <SearchBar
            placeholder="Search for a patient's tasks"
            onSearch={list.handleTaskSearch}
            onResults={undefined}
            className="tasks-search-bar"
            clearSearch={list.setClearSearchBarFunction}
          />
        </div>
      </div>

      {list.loading ? (
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading tasks...</p>
        </div>
      ) : (
        <div className="tasks-table-container">
          {list.isSearchActive && (
            <div className="tasks-search-results-info">
              Found {list.searchResults.length} task{list.searchResults.length !== 1 ? 's' : ''} matching "{list.searchQuery}"
            </div>
          )}

          <Table
            id="OutcomeTasksTable"
            data={list.sortedTasks}
            columns={table.columns}
            columnOrder={table.columnOrder}
            hiddenColumns={table.hiddenColumnsSet}
            settingsButton={<ColumnCustomizationButton onClick={() => table.setColumnCustomizationOpen(true)} />}
            getRowKey={(task) => task.taskId}
            sortState={table.tableSortState}
            onSortChange={table.setTableSortState}
            filterValues={table.tableFilterValues}
            onFilterChange={table.setTableFilterValues}
            disabledFilters={table.tableDisabledFilters}
            onDisabledFiltersChange={table.setTableDisabledFilters}
            paginationState={table.tablePaginationState}
            onPaginationChange={table.setTablePaginationState}
            onRowClick={handleRowClick}
            enableSelection={true}
            bulkActions={table.bulkActions}
            selectedRows={table.selectedRows}
            onSelectionChange={table.setSelectedRows}
            highlightedRows={table.highlightedRows}
            onHighlightChange={table.setHighlightedRows}
            emptyMessage="No outcome tasks found."
          />
        </div>
      )}

      <TaskModal
        isOpen={list.modalOpen}
        onClose={list.closeTask}
        task={list.selectedTask}
        isReadOnly={list.selectedTask?.isComplete}
        onSave={list.handleSaveTask}
        onMarkComplete={list.handleMarkComplete}
        onDelete={list.handleDeleteTask}
        loading={list.modalLoading}
        activeUsers={list.activeUsers}
        teams={list.teams}
        getAssigneeName={list.getAssigneeName}
        getViewerCount={getViewerCount}
        getViewers={getViewers}
      />

      <BulkDeleteTasksModal
        isOpen={table.bulkDeleteModalOpen}
        onClose={() => {
          table.setBulkDeleteModalOpen(false);
          table.setTasksForBulkAction([]);
        }}
        onConfirm={table.confirmBulkDeleteTasks}
        tasks={table.tasksForBulkAction}
        loading={table.bulkDeleteLoading}
      />

      <BulkMarkCompleteTasksModal
        isOpen={table.bulkMarkCompleteModalOpen}
        onClose={() => {
          table.setBulkMarkCompleteModalOpen(false);
          table.setTasksForBulkAction([]);
        }}
        onConfirm={table.confirmBulkMarkComplete}
        tasks={table.tasksForBulkAction}
        loading={table.bulkMarkCompleteLoading}
      />

      <ColumnCustomizationModal
        isOpen={table.columnCustomizationOpen}
        onClose={() => table.setColumnCustomizationOpen(false)}
        columns={table.columns}
        currentOrder={table.columnOrder}
        hiddenColumns={table.hiddenColumnsSet}
        defaultHidden={table.defaultHidden}
        onSave={(order, hidden) => {
          table.saveSettings(order, Array.from(hidden));
          table.setColumnCustomizationOpen(false);
        }}
      />
    </div>
  );
};

export default OutcomeTaskViewController;
